// import { SOCKET_URL } from "utils/constants";
import { io, Socket } from "socket.io-client";
import { SocketContext } from "./ScoketContext";
import { useState, ReactNode, useEffect } from "react";
import { useTokens } from "hooks/useToken";
import { NORMALIZED_SUPPLY_TOKENS } from "utils/constants";
import { useApp } from "providers";
import { CommentType } from "types/comment.type";
import { LatestTradeType } from "types/token.type";
import { channelToSubscription, getNextBarTime } from "components/TVChart/streaming";
import { normalizeLatestTrade, normalizeToken } from "utils/helpers";

interface SocketProviderProps {
  children: ReactNode;
}

const SocketProvider = ({ children }: SocketProviderProps) => {
  const { setLatestTrade, setTokens, pepeTokenPrice } = useApp();
  const [socket, setSocket] = useState<Socket | null>(null);
  const { tokens, refetch } = useTokens();

  useEffect(() => {
    const newSocket = io(import.meta.env.VITE_SOCKET_URL, {
      transports: ["websocket"],
      secure: true,
      rejectUnauthorized: false
    });
    newSocket.on("connect", async () => {
      console.log(" --@ connected to backend", newSocket.id);
    });
    setSocket(newSocket);
    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on("message", (data) => {
      const trade = JSON.parse(data);
      const subscriptionItem = channelToSubscription.get(trade.token);
      const tokenState = tokens.find((tk) => tk.token === trade.token);
      const tokenSupply = Number(tokenState?.tokenSupply ?? 0);
      const priceScale = tokenSupply > 0 ? NORMALIZED_SUPPLY_TOKENS / tokenSupply : 1;
      const basePrice = parseFloat(trade.price);
      const tradePrice = Number.isFinite(basePrice) ? basePrice * priceScale : basePrice;
      trade.price = tradePrice;
      const tradeTime = new Date(trade.timestamp).getTime();

      if (subscriptionItem === undefined) {
        return;
      }
      const lastBar = subscriptionItem.lastBar;
      const nextBarTime = getNextBarTime(lastBar.time, parseInt(subscriptionItem.resolution));
      let bar;
      if(tradeTime >= nextBarTime){
        const prevClose = lastBar.close ?? tradePrice;
        bar = {
          time: nextBarTime,
          open: prevClose,
          high: Math.max(prevClose, tradePrice),
          low: Math.min(prevClose, tradePrice),
          close: tradePrice,
        };
      } else {
        bar = {
          ...lastBar,
          high: Math.max(lastBar.high, tradePrice),
          low:Math.min(lastBar.low, tradePrice),
          close: tradePrice
        };
      }
      subscriptionItem.lastBar = bar;
      subscriptionItem.handlers.forEach((handler) => {
        handler.callback(bar);
      });
    });
    socket.on("TokenCreated", (mint: string) => {
      refetch();
    });

    // Listen for trade updates
    socket.on("TradeUpdated", (data: LatestTradeType) => {
      if (data && data.token && data.amount) {
        const tokenState = tokens.find((tk) => tk.token === data.token);
        const tokenSupply = Number(tokenState?.tokenSupply ?? 0);
        const priceScale = tokenSupply > 0 ? NORMALIZED_SUPPLY_TOKENS / tokenSupply : 1;
        if (typeof data.price === "number") {
          data.price = data.price * priceScale;
        } else if (typeof data.price === "string") {
          const parsed = parseFloat(data.price);
          data.price = Number.isFinite(parsed) ? parsed * priceScale : parsed;
        }

        setLatestTrade(normalizeLatestTrade(data, { ethPriceUsd: pepeTokenPrice }));
        if (Array.isArray(tokens) && tokens.length > 0) {
          setTokens(
            tokens.map((token) =>
              token.token === data.token
                ? normalizeToken({ ...token, ...data }, pepeTokenPrice)
                : token
            )
          );
        }
        setTimeout(() => {
          refetch();
        }, 1000);
      }
    });

    // Listen for comment updates
    socket.on("MessageUpdated", (tokenAddress: string, updatedComment: CommentType) => {
      // You can dispatch this to your global state management if needed
      // For example, using a context or Redux
    });

    return () => {
      socket.off("message");
      socket.off("TokenCreated");
      socket.off("TradeUpdated");
      socket.off("MessageUpdated");
    };
  }, [socket, setLatestTrade, setTokens, tokens, pepeTokenPrice, refetch]);

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;
